import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const parsed = payload as ApiErrorPayload;
    const message = parsed.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

interface CountryRecord {
  id?: unknown;
  name?: unknown;
}

function isCountryRecord(value: unknown): value is Required<CountryRecord> {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as CountryRecord;
  return (
    (typeof record.id === 'number' || typeof record.id === 'string') &&
    typeof record.name === 'string'
  );
}

export interface CountryOption {
  id: string;
  code: string; // Maintains legacy field name expected by CityForm
  name: string;
  name_en: string;
}

async function fetchCountries(): Promise<CountryOption[]> {
  const response = await apiFetch('/api/v1/countries');
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(
      payload,
      `Failed to load countries: ${response.status} ${response.statusText}`
    );
    throw new Error(message);
  }

  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.filter(isCountryRecord).map((item) => {
    const id = String(item.id);
    const name = item.name as string;
    return {
      id,
      code: id,
      name,
      name_en: name,
    };
  });
}

export function useCountries() {
  return useQuery({
    queryKey: queryKeys.countries,
    queryFn: fetchCountries,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
}
